import type { Domain, ParsedRow } from "@/api/types";
import { isJsonObject, type JsonValue } from "@/api/json";
import { money } from "@/components/format";
import { Empty } from "@/components/Page";

type ValueFormat = "text" | "date" | "money" | "boolean";
interface PreviewColumn {
  key: string;
  label: string;
  format?: ValueFormat;
}

const RECORD_LABELS: Record<string, string> = {
  bank_movement: "Movimientos del extracto",
  ledger_entry: "Movimientos del mayor",
  card_settlement: "Liquidaciones de tarjetas",
  ota_receivable: "Reservas y cobranzas OTA"
};

const RECORD_COLUMNS: Record<string, PreviewColumn[]> = {
  bank_movement: [
    { key: "transactionDate", label: "Fecha", format: "date" },
    { key: "description", label: "Descripción" },
    { key: "reference", label: "Referencia" },
    { key: "checkNumber", label: "Cheque" },
    { key: "debit", label: "Débito", format: "money" },
    { key: "credit", label: "Crédito", format: "money" },
    { key: "balance", label: "Saldo", format: "money" },
    { key: "currency", label: "Moneda" }
  ],
  ledger_entry: [
    { key: "transactionDate", label: "Fecha", format: "date" },
    { key: "voucherType", label: "Comprobante" },
    { key: "reference", label: "Referencia" },
    { key: "description", label: "Descripción" },
    { key: "debit", label: "Debe", format: "money" },
    { key: "credit", label: "Haber", format: "money" },
    { key: "balance", label: "Saldo", format: "money" },
    { key: "currency", label: "Moneda" }
  ],
  card_settlement: [
    { key: "provider", label: "Entidad" },
    { key: "settlementNumber", label: "Liquidación" },
    { key: "operationDate", label: "Operación", format: "date" },
    { key: "couponNumber", label: "Cupón" },
    { key: "lote", label: "Lote" },
    { key: "presentationDate", label: "Presentación", format: "date" },
    { key: "paymentDate", label: "Pago", format: "date" },
    { key: "grossAmount", label: "Bruto", format: "money" },
    { key: "feeAmount", label: "Aranceles", format: "money" },
    { key: "vatOnFee", label: "IVA s/arancel", format: "money" },
    { key: "grossIncomeWithholding", label: "IIBB", format: "money" },
    { key: "vatWithholding", label: "Ret. IVA", format: "money" },
    { key: "gainsWithholding", label: "Ret. Ganancias", format: "money" },
    { key: "vatPerception", label: "Perc. IVA", format: "money" },
    { key: "posnet", label: "Posnet", format: "money" },
    { key: "otherDeductions", label: "Otros", format: "money" },
    { key: "totalDeductions", label: "Total deducciones", format: "money" },
    { key: "netAmount", label: "Neto", format: "money" },
    { key: "currency", label: "Moneda" }
  ],
  ota_receivable: [
    { key: "agency", label: "Agencia" },
    { key: "reservationId", label: "Reserva" },
    { key: "locator", label: "Localizador" },
    { key: "guestName", label: "Huésped" },
    { key: "arrivalDate", label: "Ingreso", format: "date" },
    { key: "departureDate", label: "Salida", format: "date" },
    { key: "totalAmount", label: "Total", format: "money" },
    { key: "currency", label: "Moneda" },
    { key: "state", label: "Estado" },
    { key: "collected", label: "Cobrado", format: "boolean" },
    { key: "operationsNumber", label: "N.º operación" },
    { key: "contact", label: "Contacto" }
  ]
};

const FIELD_LABELS: Record<string, string> = {
  transactionDate: "Fecha", paymentDate: "Fecha de pago", presentationDate: "Presentación",
  operationDate: "Operación", couponNumber: "Cupón", lote: "Lote",
  description: "Descripción", reference: "Referencia", checkNumber: "Cheque", voucherType: "Comprobante",
  debit: "Débito", credit: "Crédito", balance: "Saldo", currency: "Moneda",
  grossAmount: "Bruto", fees: "Aranceles", feeAmount: "Aranceles", vat: "IVA", vatOnFee: "IVA s/arancel",
  grossIncomeWithholding: "IIBB", vatWithholding: "Ret. IVA", gainsWithholding: "Ret. Ganancias",
  vatPerception: "Perc. IVA", posnet: "Posnet", otherDeductions: "Otros",
  totalDeductions: "Total deducciones", withholdings: "Retenciones", netAmount: "Neto",
  agency: "Agencia", reservationId: "Reserva", locator: "Localizador", guestName: "Huésped",
  arrivalDate: "Ingreso", departureDate: "Salida", totalAmount: "Total", collected: "Cobrado"
};

const humanize = (key: string) => {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  const words = key.replace(/([a-z])([A-Z])/g, "$1 $2").replaceAll("_", " ").toLocaleLowerCase("es-AR");
  return words.replace(/^./, (letter) => letter.toLocaleUpperCase("es-AR"));
};

const fallbackFormat = (key: string, value: JsonValue | undefined): ValueFormat => {
  if (/date$/i.test(key)) return "date";
  if (/amount|debit|credit|balance|fees|vat|withholding/i.test(key)) return "money";
  if (typeof value === "boolean") return "boolean";
  return "text";
};

const formatDate = (value: JsonValue | undefined) => {
  if (!value) return "—";
  const date = new Date(String(value));
  return Number.isNaN(date.valueOf()) ? String(value) : new Intl.DateTimeFormat("es-AR", { timeZone: "UTC" }).format(date);
};

const textValue = (value: JsonValue | undefined): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.map(textValue).join(", ");
  if (isJsonObject(value)) return Object.entries(value).map(([key, item]) => `${humanize(key)}: ${textValue(item)}`).join(" · ");
  return String(value);
};

export function formatPreviewValue(value: JsonValue | undefined, format: ValueFormat, currency = "ARS") {
  if (value === null || value === undefined || value === "") return "—";
  if (format === "date") return formatDate(value);
  if (format === "boolean") return Boolean(value) ? "Sí" : "No";
  if (format === "money") {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? money(numeric, currency || "ARS") : textValue(value);
  }
  return textValue(value);
}

const fallbackColumns = (rows: ParsedRow[]) => Array.from(new Set(rows.flatMap((row) => Object.keys(row.normalizedData)))).map((key) => {
  const example = rows.find((row) => row.normalizedData[key] !== undefined)?.normalizedData[key];
  return { key, label: humanize(key), format: fallbackFormat(key, example) } satisfies PreviewColumn;
});

function RecordTable({ recordType, rows, domain }: { recordType: string; rows: ParsedRow[]; domain?: Domain }) {
  const columns = (RECORD_COLUMNS[recordType] ?? fallbackColumns(rows))
    .filter((column) => domain !== "BANK" || column.key !== "balance");
  const bankTable = domain === "BANK" && ["bank_movement", "ledger_entry"].includes(recordType);
  return <section className="normalized-records" aria-label={RECORD_LABELS[recordType] ?? `Registros ${recordType}`}>
    <div className="normalized-records__heading"><strong>{RECORD_LABELS[recordType] ?? humanize(recordType)}</strong><span>{rows.length} {rows.length === 1 ? "registro" : "registros"} en esta página</span></div>
    <div className={`table-wrap table-wrap--preview${bankTable ? " table-wrap--bank" : ""}`}><table className={`normalized-table${bankTable ? " normalized-table--bank" : ""}`}><thead><tr><th className="normalized-cell--row">Fila</th>{columns.map((column) => <th key={column.key} className={`normalized-cell--${column.key}${column.format === "money" ? " is-numeric" : ""}`}>{column.label}</th>)}</tr></thead><tbody>{rows.map((row) => {
      const currency = String(row.normalizedData.currency || "ARS");
      const status = row.status === "WARNING" ? "con advertencias" : row.status === "ERROR" ? "con errores" : "válida";
      return <tr key={row.id} aria-label={`Fila ${row.rowNumber}, ${status}`} className={`normalized-row normalized-row--${row.status.toLowerCase()}`}><td className="mono normalized-cell--row" data-label="Fila">{row.rowNumber}</td>{columns.map((column) => <td key={column.key} data-label={column.label} className={`normalized-cell--${column.key}${column.format === "money" ? " mono is-numeric" : ""}`}>{formatPreviewValue(row.normalizedData[column.key], column.format ?? "text", currency)}</td>)}</tr>;
    })}</tbody></table></div>
  </section>;
}

export function NormalizedDataTable({ rows, domain }: { rows: ParsedRow[]; domain?: Domain }) {
  if (!rows.length) return <Empty>No hay filas normalizadas para mostrar.</Empty>;
  const groups = Array.from(new Set(rows.map((row) => row.recordType))).map((recordType) => ({ recordType, rows: rows.filter((row) => row.recordType === recordType) }));
  return <div className="normalized-preview">{groups.map((group) => <RecordTable key={group.recordType} {...group} domain={domain}/>)}</div>;
}
